import { useSiteData } from '../context/DataContext.jsx'
import RailBlock from '../components/RailBlock.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'

function EducationPage() {
  const { data: siteData, loading, error, refetch } = useSiteData()
  const data = siteData?.education || []

  if (loading) {
    return (
      <div className="pg-layout">
        <div>
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-14 w-80 mb-2" />
          <Skeleton className="h-5 w-96 mb-10" />
          {[1, 2].map((i) => (
            <div key={i} className="border-t border-soft-rule py-8">
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_240px] gap-4 sm:gap-8">
                <div className="space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-6 w-56" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-full max-w-[620px]" />
                </div>
                <div className="justify-self-end space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-7 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load education" onRetry={refetch} />

  const educations = data || []

  return (
    <div className="pg-layout">
      {/* LEFT */}
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">
          Education · two entries
        </p>
        <h1 className="text-[36px] sm:text-[48px] md:text-[56px] font-normal leading-[1.02] tracking-[-1.8px] mb-4">
          Two degrees. Five years.
        </h1>
        <p className="text-[18px] leading-[1.5] text-soft-dim max-w-[620px] mb-10">
          A B.Tech in Computer Science from Amrita, and an M.Sc. in AI from DCU.
        </p>

        <div>
          {educations.map((edu, index) => (
            <div
              key={edu.id}
              className={`grid grid-cols-1 sm:grid-cols-[120px_1fr_240px] gap-6 sm:gap-8 pt-8 pb-9 ${
                index > 0 ? 'border-t border-soft-rule' : 'border-t border-soft-rule'
              }`}
            >
              <div>
                <span className="font-mono text-[12px] text-soft-dim block">
                  {edu.start_year}–{edu.end_year || 'present'}
                </span>
                {edu.status && (
                  <span className="font-mono text-[11px] uppercase tracking-[0.5px] text-soft-faint block mt-1">
                    {edu.status === 'in_progress'
                      ? 'IN PROGRESS'
                      : edu.status === 'completed'
                      ? 'COMPLETED'
                      : edu.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>

              <div>
                <h2 className="text-[24px] font-medium leading-[1.2] tracking-[-0.5px] text-soft-ink mb-1">
                  {edu.degree}
                </h2>
                <p className="text-[16px] text-soft-ink mb-1">
                  {edu.institution}
                  <span className="text-soft-faint mx-1.5">·</span>
                  <span className="text-soft-dim">{edu.field}</span>
                </p>

                {edu.modules_list?.length > 0 && (
                  <div>
                    <p className="font-mono text-[10.5px] uppercase tracking-[0.6px] text-soft-faint mb-2.5">
                      Selected modules
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {edu.modules_list.map((mod) => (
                        <div key={mod} className="flex items-start gap-2">
                          <span className="font-mono text-[12px] text-soft-faint mt-0.5">-</span>
                          <span className="text-[14px] text-soft-ink">{mod}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="sm:text-right sm:self-start mt-2 sm:mt-0">
                <p className="font-mono text-[10.5px] uppercase tracking-[0.6px] text-soft-faint mb-1.5">
                  Grade
                </p>
                <p className="text-[28px] font-normal leading-[1] tracking-[-0.8px] text-soft-ink">
                  {edu.grade || '-'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT rail */}
      <aside>

        <RailBlock eyebrow="Outside class">
          <p className="text-[13.5px] text-soft-ink leading-[1.55]">
            Working on getting certified by AWS, for Cloud solution architect certification.
            Open-source contributions projects i use and like. Exploring the new pioneering tech and keeping myself upskilled.
          </p>
        </RailBlock>

        <RailBlock eyebrow="Languages">
          <div className="divide-y divide-soft-rule">
            {[
              { lang: 'English', level: 'FLUENT' },
              { lang: 'Tamil', level: 'NATIVE' },
              { lang: 'Hindi', level: 'CONVERSATIONAL' },
              { lang: 'Malayalam', level: 'BASIC' },
            ].map((item) => (
              <div key={item.lang} className="flex justify-between items-center py-2.5">
                <span className="text-[13.5px] text-soft-ink">{item.lang}</span>
                <span className="font-mono text-[11px] text-soft-faint">{item.level}</span>
              </div>
            ))}
          </div>
        </RailBlock>
      </aside>
    </div>
  )
}

export default EducationPage
